import { ChipConfig } from '@components/v2/form/_components/ChipList/chipList.types';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
} from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { forwardRef, useId } from 'react';
import SelectChips from './components/SelectChips/SelectChips';
import useLockedSelectChange from './hooks/useLockedSelectChange';
import { SelectOption, SelectOptionValue } from './select.types';
import { findOptionLabel, renderSelectOptions } from './select.utils';

type MultipleSelectionProps = {
  onDelete?: (value: SelectOptionValue) => void;
  getChipConfig?: (value: SelectOptionValue) => ChipConfig | undefined;
};

type BaseProps = Pick<
  MuiSelectProps,
  | 'disabled'
  | 'error'
  | 'label'
  | 'value'
  | 'onChange'
  | 'onBlur'
  | 'onFocus'
  | 'defaultOpen'
  | 'className'
  | 'sx'
  | 'autoFocus'
  | 'name'
  | 'id'
  | 'required'
  | 'size'
  | 'fullWidth'
  | 'multiple'
  | 'displayEmpty'
  | 'renderValue'
  | 'open'
  | 'onClose'
> & {
  helperText?: React.ReactNode;
  multipleProps?: MultipleSelectionProps;
  testId?: string;
};

type Props = BaseProps &
  (
    | { options: SelectOption[]; children?: never }
    | { options?: never; children: MuiSelectProps['children'] }
  );

const shrinkForDisplayEmpty = (displayEmpty?: boolean) => displayEmpty || undefined;

const Select = forwardRef<HTMLInputElement, Props>(
  (
    {
      size = 'medium',
      fullWidth = true,
      disabled,
      error,
      label,
      value,
      onChange,
      onBlur,
      onFocus,
      defaultOpen,
      className,
      sx,
      autoFocus,
      name,
      id,
      required,
      multiple,
      displayEmpty,
      renderValue,
      open,
      onClose,
      multipleProps,
      helperText,
      options,
      testId,
      children,
    },
    ref,
  ) => {
    const generatedId = useId();
    const labelId = `${id ?? generatedId}-label`;

    const handleChange = useLockedSelectChange({
      value,
      multiple,
      getChipConfig: multipleProps?.getChipConfig,
      onChange,
    });

    return (
      <FormControl
        variant="outlined"
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        error={error}
        required={required}
        className={className}
        sx={sx}
      >
        {label !== undefined && (
          <InputLabel id={labelId} shrink={shrinkForDisplayEmpty(displayEmpty)}>
            {label}
          </InputLabel>
        )}
        <MuiSelect
          labelId={label !== undefined ? labelId : undefined}
          id={id}
          label={label}
          size={size}
          value={value}
          multiple={multiple}
          displayEmpty={displayEmpty}
          notched={shrinkForDisplayEmpty(displayEmpty)}
          open={open}
          autoFocus={autoFocus}
          defaultOpen={defaultOpen}
          name={name}
          inputRef={ref}
          IconComponent={ChevronDown}
          SelectDisplayProps={
            testId ? ({ 'test-id': testId } as React.HTMLAttributes<HTMLDivElement>) : undefined
          }
          onChange={handleChange}
          onClose={onClose}
          onBlur={onBlur}
          onFocus={onFocus}
          renderValue={
            renderValue ??
            (multiple
              ? (selected) => (
                  <SelectChips
                    selected={selected as SelectOptionValue[]}
                    onItemDelete={multipleProps?.onDelete}
                    getChipConfig={multipleProps?.getChipConfig}
                    getOptionLabel={options ? (item) => findOptionLabel(options, item) : undefined}
                  />
                )
              : undefined)
          }
        >
          {options ? renderSelectOptions({ options, multiple, value }) : children}
        </MuiSelect>
        {helperText !== undefined && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  },
);

Select.displayName = 'Select';
export type { Props as SelectProps };
export default Select;
